import { LOCAL_STORAGE_KEY } from "@/constants/localStorageKey";
import useAppStore from "@/context/store";
import {
  createPins,
  getBoard,
  getGrocery,
  getMyMuseStatus,
  updateBoard,
  uploadFile,
} from "@/services";

import { HB_CHECK_PROMPT } from "@/constants/cacheKey";
import { uploadTracker } from "@/tracker/report/uploadTtacker";
import { parallel } from "@/utils/concurrency";
import { gdSlsReportError } from "@/utils/gdSlsRum";
import { isEmpty } from "@/utils/isEmpty";
import { useLocalStorageState, useMemoizedFn, useMount } from "ahooks";
import lodash from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import HBMessage from "../../HBMessage";
import { STATUS as UPLOAD_STATUS } from "../PinUploader/ImageAvatar/index";
const tipError = lodash.debounce(HBMessage.error, 500);
const tipInfo = lodash.debounce(HBMessage.info, 500);

export default function useViewModel(config) {
  const {
    onOk,
    onCancel,
    targetBoardId,
    onUploadedFileChange,
    onlyCreation: _onlyCreation,
    fileList = [],
    isAiUpload = false,
  } = config;

  const [selectedResource, setSelectedResource] = useState();
  const [resources, setResources] = useState([]);
  const [, setResourcesUpdateAt] = useState(Date.now());
  const [boardId, setBoardId] = useState(targetBoardId);
  const [categoryId, setCategoryId] = useState(null);
  const [targetBoard, setTargetBoard] = useState();
  const [maxUploadAmount, setMaxUploadAmount] = useState(0);
  const [isMuseUser, setMuseUser] = useState(false);
  const [tel, setTel] = useState();
  const [isMuseVerifying, setMuseVerifying] = useState(false);
  const [canUpload, setCanUpload] = useState(true);
  const [syncTagsChecked, setSyncTagsChecked] = useState(false);
  const [globalTags, setGlobalTags] = useState(null);

  const abortControllersRef = useRef([]);

  const {
    categories,
    aiLinkMap,
    config: globalConfig,
    setStore,
  } = useAppStore(({ categories, aiLinkMap, setStore, config }) => ({
    categories,
    aiLinkMap,
    setStore,
    config,
  }));

  const [verifyVisible, setVerifyVisible] = useState(false);
  const myBoardSelectRef = useRef(null);
  const [checkPrompt, setCheckPrompt] = useLocalStorageState(HB_CHECK_PROMPT, {
    defaultValue: true,
  });
  const currentResource = resources.find(
    (r) => r.uid === selectedResource?.uid
  );

  const uploadTasks = useRef([]);
  const needCheckAi = useMemo(() => {
    /**
     * 只有以下情况需要校验
     * 1.当用户处于ai页面点击瀑布流里面的上传，且没有勾选不再提醒AI作品信息
     */
    return isAiUpload && checkPrompt;
  }, [isAiUpload, checkPrompt]);

  const onlyCreation = useMemo(
    () => !!targetBoard?.extra?.is_creation || !!_onlyCreation,
    [targetBoard, _onlyCreation]
  );
  const execUploadTasks = useCallback(() => {
    const tasks = uploadTasks.current;
    uploadTasks.current = [];
    parallel(
      tasks.map((resource) => async () => {
        const { uid, file } = resource;
        resource.status = UPLOAD_STATUS.UPLOADING;
        resource.progress = 0;
        setResourcesUpdateAt(Date.now());

        try {
          const controller = window.AbortController
            ? new AbortController()
            : null;
          abortControllersRef.current.push(controller);

          const result = await uploadFile(
            file,
            ({ loaded, total }) => {
              resource.progress = (loaded / total) * 100;
              setResourcesUpdateAt(Date.now());
            },
            controller?.signal
          );

          if (!result.err) {
            resource.ossFile = result;
            resource.status = UPLOAD_STATUS.DONE;
            // 判断图片是否能被识别出promot,能识别出则不允许修改
            resource.hasOriginalPromot = !!result.aigc?.prompt;
            resource.hasOriginalCategory = !!result.aigc?.aigc_category;

            // if(result.)
            // resource.originalPromot
          } else {
            console.error("上传失败：", uid, resource.name);
            resource.status = UPLOAD_STATUS.FAILURE;
            // if (typeof window !== "undefined" && window._SLS_) {
            //   window._SLS_.reportError(
            //     new Error("上传文件失败:" + result.err.toString())
            //   );
            // }
            gdSlsReportError(
              new Error("上传文件失败:" + result.err.toString())
            );
          }
        } catch (err) {
          console.error("上传失败：", uid, resource.name);
          resource.status = UPLOAD_STATUS.FAILURE;
          gdSlsReportError(new Error("上传文件失败:" + err.toString()));
        }
        setResourcesUpdateAt(Date.now());
      }),
      5
    );
  }, []);

  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      abortControllersRef.current?.forEach((controller) => {
        controller?.abort();
      });
    };
  }, []);

  const onResourceSelect = useCallback(
    (resource) => () => setSelectedResource(resource || null),
    []
  );
  const onCancelClick = useCallback(() => {
    onCancel && onCancel();
  }, [onCancel]);
  // 校验流程
  const verification = (list) => {
    /**
     * 1.如果在ai圈内，且未勾选不再提醒则校验
     * 2.不再ai圈内则不需要校验信息
     */
    // 拿到缺失prompt的项
    let hiatusPrompt = false;
    let hasAiImg = false;
    list?.forEach((item) => {
      // 如果有prompt
      if (!isEmpty(item.ossFile?.aigc?.prompt)) {
        hasAiImg = true;
      } else {
        hiatusPrompt = true;
      }
    });

    return { hiatusPrompt, hasAiImg };
  };
  const onOkClick = useCallback(
    async (needCheck = true) => {
      if (!canUpload) return;

      const list = resources.map((item) => {
        item.tags = globalTags || item.tags;
        return item;
      });
      // 判断是否有prompt为空,和是否有ai图片
      let { hiatusPrompt, hasAiImg } = verification(list);

      // needCheck是为了满足用户点击上传为普通采集
      if (needCheckAi && needCheck && hiatusPrompt) {
        setVerifyVisible(true);
        return;
      }
      setCanUpload(false);
      const data = {
        ...(boardId <= 0 ? null : { board_id: boardId }),
        pins: list.map((r) => {
          let aigc = r.ossFile.aigc;

          // 兜底逻辑，如果没有选择创作工具类型默认为other
          if (aigc?.prompt && !aigc.aigc_category) {
            aigc.aigc_category = "other";
          }
          return {
            file_id: r.ossFile.id,
            text: r.description ?? undefined,
            tags: r.tags?.length ? r.tags : undefined,
            // 拿到prompt 再传递aigc信息
            aigc: isEmpty(aigc?.prompt) ? undefined : aigc,
          };
        }),
      };
      const { err, failMsg, result } = await createPins(
        { isMuseUser, hasTel: !!tel },
        data
      );

      // 具体图片错误处理
      let errs = [];
      const nextResources = [];
      for (const key in result) {
        const res = result[key];
        if (res.err) {
          const matches = resources.filter((r) => r.ossFile.id == key);
          if (!errs.includes(res.msg)) {
            errs.push(res.msg);
          }
          matches.forEach((r) => {
            r.status = UPLOAD_STATUS.FAILURE;
            r.failMsg = res.msg;
            nextResources.push(r);
          });
        }
      }
      if (errs.length) {
        setCanUpload(true);
        const successNum = resources.length - nextResources.length;
        if (successNum) {
          HBMessage.info(
            `上传成功${successNum}个，失败${nextResources.length}个`,
            5
          );

          uploadTracker({
            upload_status: "部分失败",
            upload_num: data.pins.length,
            upload_type: "web端",
          });
        } else {
          errs.forEach((err) => HBMessage.error(err, 5));
          uploadTracker({
            upload_status: "全部失败",
            upload_num: data.pins.length,
            upload_type: "web端",
          });
        }
        setResources(nextResources);
        setSelectedResource(nextResources[0]);

        return;
      }

      // 其他失败原因处理
      if (err) {
        setCanUpload(true);
        uploadTracker({
          upload_status: "全部失败",
          upload_num: data.pins.length,
          upload_type: "web端",
        });
        return;
      }
      if (failMsg) {
        setCanUpload(true);
        HBMessage.error(failMsg);
        return;
      }

      uploadTracker({
        upload_status: "全部成功",
        upload_num: data.pins.length,
        upload_type: "web端",
      });
      const realBoardId = Object.values(result)?.find(
        (value) => value?.board_id
      )?.board_id;
      const { board } = await getBoard({
        board_id: boardId <= 0 ? realBoardId : boardId,
        pins: 1,
      });
      onOk &&
        requestAnimationFrame(() => {
          onOk(board, list, hasAiImg);
        });
      const lastBoards = await useAppStore.getState().refreshLastBoards();
      if (lastBoards) {
        try {
          window.localStorage.setItem(
            LOCAL_STORAGE_KEY.UPDATE_LAST_BOARDS,
            JSON.stringify(lastBoards)
          );
        } catch (e) {
          console.error("设置更新最近采集失败");
        }
      }
    },
    [
      canUpload,
      resources,
      boardId,
      isMuseUser,
      tel,
      onOk,
      globalTags,
      needCheckAi,
    ]
  );

  const onReUploadFileClick = (resource) => () => {
    uploadTasks.current.push(resource);
    execUploadTasks();
  };
  const onFilePicked = lodash.debounce((_, fileList) => {
    let initSelected = !!selectedResource;
    for (const file of fileList) {
      const { name, size, lastModified } = file;
      const uid = name + size + lastModified;
      if (resources.find((r) => r.uid === uid)) {
        tipInfo("已帮你过滤重复图片");
        continue;
      }

      // if (type === "image/bmp") {
      //   tipError("尚不支持bmp格式图片上传");
      //   continue;
      // }

      if (size >= 20 * 1024 * 1024) {
        tipError(`图片【${name}】太大，单张图片体积不超过 20 MB`);
        continue;
      }

      if (resources.length >= maxUploadAmount) {
        tipInfo(`只能上传${maxUploadAmount}张图片`);
        break;
      }

      const resource = {
        uid,
        name,
        size,
        progress: 0,
        status: UPLOAD_STATUS.NEW,
        url: undefined,
        file,
        description: name.split(".").shift(),
        tags: [],
        ossFile: undefined,
        isOriginalPromot: false,
      };
      const reader = new FileReader();
      reader.addEventListener("load", function () {
        resource.url = this.result;
        setResourcesUpdateAt(Date.now());
        if (!initSelected && resources[0].url) {
          onResourceSelect(resources[0])();
          initSelected = true;
        }
      });
      reader.readAsDataURL(file);
      resources.push(resource);
      uploadTasks.current.push(resource);
    }
    setResources([...resources]);
    setResourcesUpdateAt(Date.now());
    execUploadTasks();
    onUploadedFileChange && onUploadedFileChange(resources);
  }, 500);

  const onDescriptionChange = useCallback(
    (e) => {
      if (!selectedResource) return;

      selectedResource.description = e.target.value;
      resources.find((r) => r.uid === selectedResource.uid).description =
        e.target.value;
      setSelectedResource({ ...selectedResource });
    },
    [selectedResource, resources]
  );
  const onTagsChange = useCallback(
    (value) => {
      if (!selectedResource) return;
      if (value && value.length > 10) {
        HBMessage.info("最多添加10个标签");
      }
      const tags = value.slice(0, 10);
      selectedResource.tags = tags;
      resources.find((r) => r.uid === selectedResource.uid).tags = tags;
      setSelectedResource({ ...selectedResource });
      if (syncTagsChecked) {
        setGlobalTags(tags);
      }
    },
    [selectedResource, resources, syncTagsChecked]
  );
  // ai信息修改
  const onAiInfoChange = (value, key) => {
    if (!selectedResource) return;
    let aigc = selectedResource.ossFile?.aigc || {};
    aigc[key] = value;
    setResources((preData) => {
      return preData?.map((item) => {
        if (item.uid === selectedResource.uid) {
          return { ...item, ossFile: { ...item.ossFile, aigc } };
        } else {
          return item;
        }
      });
    });
    setSelectedResource((preData) => {
      return { ...preData, ossFile: { ...preData.ossFile, aigc } };
    });
  };
  const onSyncTags = useCallback(
    (evt) => {
      const checked = evt.target.checked;
      if (checked) {
        setGlobalTags(selectedResource.tags);
      } else {
        setGlobalTags(null);
      }
      setSyncTagsChecked(checked);
    },
    [selectedResource]
  );
  const onBoardChange = useCallback((targetId, board) => {
    setCategoryId(board?.category_id);
    setBoardId(targetId);
  }, []);
  const onCategoryChange = useCallback(
    async (value) => {
      if (!boardId) {
        HBMessage.error("请先选择画板");
        return;
      }
      const { err } = await updateBoard({
        board_id: boardId,
        category: value,
      });
      if (!err) {
        HBMessage.success("画板分类修改成功");
        myBoardSelectRef.current.refresh();
        setCategoryId(value);
      }
    },
    [boardId]
  );
  const onClearBoard = useMemoizedFn(() => {
    setBoardId(null);
  });
  const onResourceRemove =
    ({ uid }) =>
    (e) => {
      e.stopPropagation();
      const newResources = resources.filter((r) => r.uid !== uid);
      if (selectedResource?.uid === uid) {
        onResourceSelect(newResources[0])();
      }
      onUploadedFileChange &&
        requestAnimationFrame(() => onUploadedFileChange(newResources));
      setResources(newResources);
      if (newResources.length <= 0 && fileList?.length > 0) {
        onCancel && onCancel();
      }
    };

  useMount(async () => {
    const [MuseStatus, result] = await Promise.all([
      getMyMuseStatus(),
      !aiLinkMap ? getGrocery("pin_detail_ai_params") : undefined,
    ]);

    const { isMuseUser, tel, status } = MuseStatus;

    if (result) {
      setStore({
        aiLinkMap: [
          ...result.linkMap?.filter((item) => item.isShow),
          { value: "other", name: "其他" },
        ],
      });
    }
    // const maxUploadAmount = isMuseUser ? (tel ? 50 : 1) : tel ? 20 : 1;
    const maxUploadAmount = 50;
    setMaxUploadAmount(maxUploadAmount);
    setMuseUser(isMuseUser);
    setTel(tel);
    setMuseVerifying(status === 4);

    if (targetBoardId) {
      const { board, err } = await getBoard({ board_id: targetBoardId });

      if (err) {
        onCancel && onCancel();
      } else {
        setTargetBoard(board);
      }
    }

    if (fileList.length > 0) {
      let initSelected = false;
      for (const file of fileList) {
        const { name, size, lastModified } = file;
        const uid = name + size + lastModified;
        if (resources.find((r) => r.uid === uid)) {
          tipInfo("已帮你过滤重复图片");
          continue;
        }

        if (size >= 20 * 1024 * 1024) {
          tipError(`图片【${name}】太大，单张图片体积不超过 20 MB`);
          continue;
        }

        if (resources.length >= maxUploadAmount) {
          tipInfo(`只能上传${maxUploadAmount}张图片`);
          break;
        }

        const resource = {
          uid,
          name,
          size,
          progress: 0,
          status: UPLOAD_STATUS.NEW,
          url: undefined,
          file,
          description: name.split(".").shift(),
          tags: [],
          ossFile: undefined,
        };
        ((resource) => {
          const reader = new FileReader();
          reader.addEventListener("load", function () {
            resource.url = this.result;
            setResourcesUpdateAt(Date.now());
            if (!initSelected && resources[0].url) {
              onResourceSelect(resources[0])();
              initSelected = true;
            }
          });
          reader.readAsDataURL(file);
          resources.push(resource);
          uploadTasks.current.push(resource);
        })(resource);
      }
      setResources([...resources]);
      setResourcesUpdateAt(Date.now());
      execUploadTasks();
      onUploadedFileChange && onUploadedFileChange(resources);
    }
  });

  useEffect(() => {
    if (targetBoard) {
      onBoardChange(targetBoard?.board_id, targetBoard);
    }
  }, [onBoardChange, targetBoard]);

  return {
    ...config,

    resources,
    selectedResource,
    currentResource,
    targetBoard,
    boardId,
    onlyCreation,
    maxUploadAmount,
    isMuseVerifying,
    canUpload,
    syncTagsChecked,
    globalTags,
    categories,
    categoryId,
    verifyVisible,
    myBoardSelectRef,
    needCheckAi, // 判断是否需要校验pormpt信息
    globalConfig,
    setCheckPrompt, // 设置用户是否勾选不再提醒
    setVerifyVisible,
    onCancelClick,
    onOkClick,
    onFilePicked,
    onDescriptionChange,
    onTagsChange,
    onBoardChange,
    onResourceRemove,
    onResourceSelect,
    onReUploadFileClick,
    onClearBoard,
    onSyncTags,
    onCategoryChange,
    onAiInfoChange,
  };
}
